import React, {useEffect, useMemo, useState} from 'react'
import {format} from 'date-fns'
import './ModalPodErrorLogs.css'

const formatLogDate = (value) => {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return '';
    }

    return format(parsedDate, 'MMM d, yyyy h:mm a');
};

const getLogLevel = (value) => String(value ?? '').toUpperCase() === 'ERROR' ? 'Error' : 'Warning';

const getLevelSortOrder = (value) => getLogLevel(value) === 'Error' ? 0 : 1;

const getLogDateValue = (logEntry) => logEntry?.latest_created_at
    ?? logEntry?.most_recent_created_at
    ?? logEntry?.last_created_at
    ?? logEntry?.created_at;

const getLogTimestamp = (logEntry) => {
    const timestamp = Date.parse(getLogDateValue(logEntry) ?? 0);

    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const compareLogCards = (leftEntry, rightEntry) => {
    const levelDifference = getLevelSortOrder(leftEntry?.level) - getLevelSortOrder(rightEntry?.level);

    return levelDifference || getLogTimestamp(rightEntry) - getLogTimestamp(leftEntry);
};

const normalizeDetailPayload = (payload) => {
    if (Array.isArray(payload)) {
        return payload[0] ?? null;
    }

    return payload && typeof payload === 'object' ? payload : null;
};

const mergeLogContext = (detailLog, fallbackLog) => {
    if (!detailLog && !fallbackLog) {
        return null;
    }

    return {
        ...(fallbackLog || {}),
        ...(detailLog || {}),
        machine_id: detailLog?.machine_id ?? fallbackLog?.machine_id ?? detailLog?.machine ?? fallbackLog?.machine ?? null,
    };
};

const ErrorLogDetailModal = ({selectedLog, onClose}) => {
    if (!selectedLog) {
        return null;
    }

    const machineSummary = selectedLog.machine_summary || {};
    const detailHeaderFields = [
        {
            label: 'Customer',
            value: selectedLog.customer_name ?? machineSummary.customer_name,
        },
        {
            label: 'Description',
            value: selectedLog.description ?? machineSummary.description,
        },
        {
            label: 'Facility',
            value: selectedLog.facility_name ?? machineSummary.facility_name,
        },
        {
            label: 'Type',
            value: selectedLog.machine_type_description ?? machineSummary.machine_type_description,
        },
    ].filter((field) => field.value !== undefined && field.value !== null && String(field.value).trim() !== '');

    return (
        <div className='pod-error-log-detail-overlay' onClick={(event) => {
            if (event.target.className === 'pod-error-log-detail-overlay') {
                onClose();
            }
        }}>
            <div className='pod-error-log-detail-modal'>
                <button className='pod-error-logs-close-btn' onClick={onClose} aria-label='Close pod error log details'>X</button>
                <h4>Error Log Detail</h4>
                {selectedLog.machine_id !== undefined && selectedLog.machine_id !== null && (
                    <div className='pod-error-log-detail-subtitle'>Pod {selectedLog.machine_id}</div>
                )}
                {detailHeaderFields.length > 0 && (
                    <div className='pod-error-log-detail-meta'>
                        {detailHeaderFields.map((field) => (
                            <div key={field.label} className='pod-error-log-detail-meta-item'>
                                <span>{field.label}</span>
                                <strong>{field.value}</strong>
                            </div>
                        ))}
                    </div>
                )}

                <div className='pod-error-log-detail-grid'>
                    <div className='pod-error-log-detail-row'><span>Date</span><span>{formatLogDate(selectedLog.created_at)}</span></div>
                    <div className='pod-error-log-detail-row'><span>Level</span><span>{selectedLog.level ?? ''}</span></div>
                    <div className='pod-error-log-detail-row'><span>Application</span><span>{selectedLog.application ?? ''}</span></div>
                    <div className='pod-error-log-detail-row'><span>Logger</span><span>{selectedLog.logger_name ?? ''}</span></div>
                    <div className='pod-error-log-detail-row'><span>Message</span><span>{selectedLog.message ?? ''}</span></div>
                    <div className='pod-error-log-detail-row'><span>Module</span><span>{selectedLog.module ?? ''}</span></div>
                    <div className='pod-error-log-detail-row'><span>Function</span><span>{selectedLog.function_name ?? ''}</span></div>
                    <div className='pod-error-log-detail-row'><span>Line Number</span><span>{selectedLog.line_number ?? ''}</span></div>
                    <div className='pod-error-log-detail-row'><span>Exception Type</span><span>{selectedLog.exception_type ?? ''}</span></div>
                    <div className='pod-error-log-detail-row'><span>Exception Message</span><span>{selectedLog.exception_message ?? ''}</span></div>
                    <div className='pod-error-log-detail-row wide'><span>Stack Trace</span><pre>{selectedLog.stack_trace ?? ''}</pre></div>
                    <div className='pod-error-log-detail-row wide'><span>Context</span><pre>{selectedLog.context ? JSON.stringify(selectedLog.context, null, 2) : ''}</pre></div>
                    <div className='pod-error-log-detail-row wide'><span>Log Context</span><pre>{Array.isArray(selectedLog.log_context) ? selectedLog.log_context.join('\n') : ''}</pre></div>
                    <div className='pod-error-log-detail-row wide'><span>Client</span><pre>{selectedLog.client ? JSON.stringify(selectedLog.client, null, 2) : ''}</pre></div>
                </div>
            </div>
        </div>
    );
};

const ModalPodErrorLogs = ({closeModal, machineId, podInfo, mode = 'machine'}) => {
    const isSummaryMode = mode === 'summary';
    const [errorLogs, setErrorLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedLog, setSelectedLog] = useState(null);
    const [loadingLogId, setLoadingLogId] = useState(null);
    const [detailErrorMessage, setDetailErrorMessage] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [groupDetails, setGroupDetails] = useState({});
    const [pendingDeleteFingerprint, setPendingDeleteFingerprint] = useState(null);
    const [deletingFingerprint, setDeletingFingerprint] = useState(null);
    const [deleteErrors, setDeleteErrors] = useState({});

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        setIsLoading(true);
        setErrorMessage('');
        setSelectedLog(null);
        setLoadingLogId(null);
        setExpandedGroups({});
        setGroupDetails({});
        setPendingDeleteFingerprint(null);
        setDeletingFingerprint(null);
        setDeleteErrors({});

        const endpoint = isSummaryMode
            ? process.env.REACT_APP_API_URL_BASE + '/logs/pod/application/summary/'
            : process.env.REACT_APP_API_URL_BASE + '/logs/pod/application/' + machineId;

        fetch(endpoint,
            {
                method: 'GET',
                headers: {
                    machine: '2',
                    'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Unable to load error logs.');
                }

                return response.json();
            })
            .then((data) => {
                if (!isMounted) {
                    return;
                }

                setErrorLogs(Array.isArray(data) ? data : []);
            })
            .catch((error) => {
                console.error(error);
                if (isMounted) {
                    setErrorMessage('Unable to load error logs right now.');
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [isSummaryMode, machineId]);

    const groupedLogs = useMemo(() => {
        if (isSummaryMode) {
            return (Array.isArray(errorLogs) ? errorLogs : []).slice().sort(compareLogCards);
        }

        const groupMap = (Array.isArray(errorLogs) ? errorLogs : []).reduce((accumulator, logEntry) => {
            const fingerprint = String(logEntry?.error_fingerprint ?? `log-${logEntry?.id ?? Math.random()}`);
            if (!accumulator[fingerprint]) {
                accumulator[fingerprint] = [];
            }

            accumulator[fingerprint].push(logEntry);
            return accumulator;
        }, {});

        return Object.values(groupMap)
            .map((entries) => entries.slice().sort((left, right) => Date.parse(right?.created_at ?? 0) - Date.parse(left?.created_at ?? 0)))
            .sort((leftGroup, rightGroup) => compareLogCards(leftGroup[0], rightGroup[0]));
    }, [errorLogs, isSummaryMode]);

    const toggleGroup = (groupKey) => {
        setExpandedGroups((previousState) => ({
            ...previousState,
            [groupKey]: !previousState[groupKey],
        }));
    };

    const handleOpenDetail = async (logId, fallbackLog = null) => {
        if (!logId) {
            setSelectedLog(mergeLogContext(null, fallbackLog));
            return;
        }

        setLoadingLogId(logId);
        setDetailErrorMessage('');

        try {
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/logs/pod/application/id/' + logId + '/', {
                method: 'GET',
                headers: {
                    machine: '2',
                    'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            });

            if (!response.ok) {
                throw new Error('Unable to load error log details.');
            }

            const data = await response.json();
            const normalizedDetail = normalizeDetailPayload(data);
            const detailWithContext = mergeLogContext(normalizedDetail, fallbackLog);

            setSelectedLog(detailWithContext);
            if (!normalizedDetail && !fallbackLog) {
                setDetailErrorMessage('Unable to load error log details right now.');
            }
        } catch (error) {
            console.error(error);
            setDetailErrorMessage('Unable to load error log details right now.');
            if (fallbackLog) {
                setSelectedLog(mergeLogContext(null, fallbackLog));
            }
        } finally {
            setLoadingLogId((currentLogId) => currentLogId === logId ? null : currentLogId);
        }
    };

    const handleToggleSummaryGroup = async (fingerprint) => {
        const groupKey = String(fingerprint ?? '');
        const isExpanded = Boolean(expandedGroups[groupKey]);

        if (isExpanded) {
            setExpandedGroups((previousState) => ({
                ...previousState,
                [groupKey]: false,
            }));
            return;
        }

        setExpandedGroups((previousState) => ({
            ...previousState,
            [groupKey]: true,
        }));

        if (groupDetails[groupKey]?.items || groupDetails[groupKey]?.isLoading) {
            return;
        }

        setGroupDetails((previousState) => ({
            ...previousState,
            [groupKey]: {
                items: [],
                isLoading: true,
                error: '',
            },
        }));

        try {
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/logs/pod/application/fingerprint/' + groupKey + '/', {
                method: 'GET',
                headers: {
                    machine: '2',
                    'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            });

            if (!response.ok) {
                throw new Error('Unable to load grouped error logs.');
            }

            const data = await response.json();
            const items = Array.isArray(data) ? data : [];

            setGroupDetails((previousState) => ({
                ...previousState,
                [groupKey]: {
                    items,
                    isLoading: false,
                    error: '',
                },
            }));
        } catch (error) {
            console.error(error);
            setGroupDetails((previousState) => ({
                ...previousState,
                [groupKey]: {
                    items: [],
                    isLoading: false,
                    error: 'Unable to load grouped error logs right now.',
                },
            }));
        }
    };

    const handleDeleteLogs = async (fingerprint) => {
        const groupKey = String(fingerprint ?? '');
        const podMachineId = machineId ?? podInfo?.id;

        if (!groupKey || podMachineId === undefined || podMachineId === null) {
            setDeleteErrors((previousState) => ({
                ...previousState,
                [groupKey]: 'These logs could not be deleted. Please try again.',
            }));
            return;
        }

        setDeletingFingerprint(groupKey);
        setDeleteErrors((previousState) => ({
            ...previousState,
            [groupKey]: '',
        }));

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL_BASE
                + '/logs/pod/application/fingerprint/' + encodeURIComponent(groupKey)
                + '/pod/' + encodeURIComponent(podMachineId),
                {
                    method: 'DELETE',
                    headers: {
                        machine: '2',
                        'vh-key': '7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                    },
                },
            );

            if (!response.ok) {
                throw new Error('Unable to delete pod error logs.');
            }

            setErrorLogs((previousLogs) => previousLogs.filter(
                (logEntry) => String(logEntry?.error_fingerprint ?? '') !== groupKey,
            ));
            setPendingDeleteFingerprint(null);
            setExpandedGroups((previousState) => {
                const nextState = {...previousState};
                delete nextState[groupKey];
                return nextState;
            });
            setDeleteErrors((previousState) => {
                const nextState = {...previousState};
                delete nextState[groupKey];
                return nextState;
            });
        } catch (error) {
            console.error('Unable to delete pod error logs.', error);
            setDeleteErrors((previousState) => ({
                ...previousState,
                [groupKey]: 'These logs could not be deleted. Please try again.',
            }));
        } finally {
            setDeletingFingerprint((currentFingerprint) => currentFingerprint === groupKey ? null : currentFingerprint);
        }
    };

    return (
        <div className='pod-error-logs-overlay' onClick={(event) => {
            if (event.target.className === 'pod-error-logs-overlay') {
                closeModal();
            }
        }}>
            <div className='pod-error-logs-modal'>
                <button className='pod-error-logs-close-btn' onClick={closeModal} aria-label='Close pod error logs'>X</button>

                <div className='pod-error-logs-header'>
                    <h3>{isSummaryMode ? 'Pod Error Log Summary' : 'Pod Error Log'}</h3>
                    <div className='pod-error-logs-subtitle'>
                        {isSummaryMode
                            ? 'Cross-pod application errors and warnings'
                            : `Pod ${podInfo?.id ?? machineId}${podInfo?.description ? ` | ${podInfo.description}` : ''}`}
                    </div>
                </div>

                {isLoading && <div className='pod-error-logs-empty'>Loading error logs...</div>}
                {!isLoading && errorMessage && <div className='pod-error-logs-error'>{errorMessage}</div>}
                {!isLoading && !errorMessage && groupedLogs.length === 0 && <div className='pod-error-logs-empty'>No error logs found.</div>}
                {!isLoading && !errorMessage && detailErrorMessage && <div className='pod-error-logs-error'>{detailErrorMessage}</div>}

                {!isLoading && !errorMessage && groupedLogs.length > 0 && (
                    <div className='pod-error-logs-groups'>
                        {isSummaryMode ? groupedLogs.map((groupEntry, groupIndex) => {
                            const groupKey = String(groupEntry?.error_fingerprint ?? `group-${groupIndex}`);
                            const isExpanded = Boolean(expandedGroups[groupKey]);
                            const detailState = groupDetails[groupKey] || { items: [], isLoading: false, error: '' };
                            const logLevel = getLogLevel(groupEntry?.level);
                            const latestOccurrence = formatLogDate(getLogDateValue(groupEntry));

                            return (
                                <section key={`${groupKey}-${groupIndex}`} className='pod-error-logs-group pod-error-logs-summary-card'>
                                    <div className='pod-error-logs-summary-card-body'>
                                        <div className='pod-error-logs-summary-card-top'>
                                            <div className='pod-error-logs-summary-card-badges'>
                                                <span className={`pod-error-logs-level-pill severity-${logLevel.toLowerCase()}`}>{logLevel}</span>
                                                <span className='pod-error-logs-count-pill'>{groupEntry?.count ?? 0}</span>
                                                {latestOccurrence && <span className='pod-error-logs-latest-occurrence'>Latest Occurrence: {latestOccurrence}</span>}
                                            </div>
                                            <button type='button' className='pod-error-logs-inline-link' onClick={() => handleToggleSummaryGroup(groupKey)} aria-expanded={isExpanded}>
                                                {isExpanded ? 'Hide' : 'Show'}
                                            </button>
                                        </div>
                                        <div className='pod-error-logs-summary-message'>{groupEntry?.message ?? 'Error Group'}</div>
                                    </div>

                                    {isExpanded && (
                                        <div className='pod-error-logs-table-wrap'>
                                            {detailState.isLoading && <div className='pod-error-logs-empty pod-error-logs-nested-state'>Loading grouped logs...</div>}
                                            {!detailState.isLoading && detailState.error && <div className='pod-error-logs-error pod-error-logs-nested-state'>{detailState.error}</div>}
                                            {!detailState.isLoading && !detailState.error && detailState.items.length === 0 && <div className='pod-error-logs-empty pod-error-logs-nested-state'>No matching logs found.</div>}
                                            {!detailState.isLoading && !detailState.error && detailState.items.length > 0 && (
                                                <table className='pod-error-logs-table'>
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Pod ID</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detailState.items.map((logEntry) => {
                                                            const isRowLoading = loadingLogId !== null && String(loadingLogId) === String(logEntry.id);

                                                            return (
                                                            <tr key={logEntry.id} className={isRowLoading ? 'is-loading' : ''} aria-busy={isRowLoading} onClick={() => {
                                                                if (!isRowLoading) {
                                                                    handleOpenDetail(logEntry.id, logEntry);
                                                                }
                                                            }}>
                                                                {isRowLoading ? (
                                                                    <td colSpan='2' className='pod-error-logs-row-loading'>Loading error log details...</td>
                                                                ) : (
                                                                    <>
                                                                        <td>{formatLogDate(logEntry.created_at)}</td>
                                                                        <td>{logEntry.machine_id ?? ''}</td>
                                                                    </>
                                                                )}
                                                            </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </section>
                            );
                        }) : groupedLogs.map((groupEntries, groupIndex) => (
                            <section key={`${groupEntries[0]?.error_fingerprint ?? 'group'}-${groupIndex}`} className='pod-error-logs-group'>
                                {(() => {
                                    const groupKey = String(groupEntries[0]?.error_fingerprint ?? `group-${groupIndex}`);
                                    const isExpanded = Boolean(expandedGroups[groupKey]);
                                    const logLevel = getLogLevel(groupEntries[0]?.level);
                                    const latestOccurrence = formatLogDate(getLogDateValue(groupEntries[0]));
                                    const isConfirmingDelete = pendingDeleteFingerprint === groupKey;
                                    const isDeleting = deletingFingerprint === groupKey;

                                    return (
                                        <>
                                            <button type='button' className='pod-error-logs-group-header' onClick={() => toggleGroup(groupKey)} aria-expanded={isExpanded}>
                                                <span className='pod-error-logs-group-header-top'>
                                                    <span className='pod-error-logs-group-header-badges'>
                                                        <span className={`pod-error-logs-level-pill severity-${logLevel.toLowerCase()}`}>{logLevel}</span>
                                                        <span className='pod-error-logs-count-pill'>{groupEntries.length}</span>
                                                        {latestOccurrence && <span className='pod-error-logs-latest-occurrence'>Latest Occurrence: {latestOccurrence}</span>}
                                                    </span>
                                                    <span className='pod-error-logs-expand-indicator'>{isExpanded ? 'Hide' : 'Show'}</span>
                                                </span>
                                                <span className='pod-error-logs-group-title'>{groupEntries[0]?.message ?? 'Error Group'}</span>
                                            </button>

                                            <div className='pod-error-logs-clear-controls'>
                                                {!isConfirmingDelete && (
                                                    <button type='button' className='pod-error-logs-clear-btn' onClick={() => {
                                                        setPendingDeleteFingerprint(groupKey);
                                                        setDeleteErrors((previousState) => ({...previousState, [groupKey]: ''}));
                                                    }}>
                                                        Clear these logs
                                                    </button>
                                                )}

                                                {isConfirmingDelete && (
                                                    <div className='pod-error-logs-delete-confirmation' role='alert'>
                                                        <span>This will delete these logs from the database. Are you sure?</span>
                                                        <div className='pod-error-logs-delete-actions'>
                                                            <button type='button' className='pod-error-logs-delete-confirm-btn' disabled={isDeleting} onClick={() => handleDeleteLogs(groupKey)}>
                                                                {isDeleting ? 'Deleting...' : 'Yes, clear logs'}
                                                            </button>
                                                            <button type='button' className='pod-error-logs-delete-cancel-btn' disabled={isDeleting} onClick={() => setPendingDeleteFingerprint(null)}>
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {deleteErrors[groupKey] && <div className='pod-error-logs-delete-error' role='alert'>{deleteErrors[groupKey]}</div>}
                                            </div>

                                            {isExpanded && (
                                                <div className='pod-error-logs-table-wrap'>
                                                    <table className='pod-error-logs-table'>
                                                        <thead>
                                                            <tr>
                                                                <th>Date</th>
                                                                <th>Level</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {groupEntries.map((logEntry) => {
                                                                const isRowLoading = loadingLogId !== null && String(loadingLogId) === String(logEntry.id);

                                                                return (
                                                                <tr key={logEntry.id} className={isRowLoading ? 'is-loading' : ''} aria-busy={isRowLoading} onClick={() => {
                                                                    if (!isRowLoading) {
                                                                        handleOpenDetail(logEntry.id, logEntry);
                                                                    }
                                                                }}>
                                                                    {isRowLoading ? (
                                                                        <td colSpan='2' className='pod-error-logs-row-loading'>Loading error log details...</td>
                                                                    ) : (
                                                                        <>
                                                                            <td>{formatLogDate(logEntry.created_at)}</td>
                                                                            <td>{logEntry.level ?? ''}</td>
                                                                        </>
                                                                    )}
                                                                </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </section>
                        ))}
                    </div>
                )}

                <ErrorLogDetailModal selectedLog={selectedLog} onClose={() => setSelectedLog(null)} />
            </div>
        </div>
    )
}

export default ModalPodErrorLogs